import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  RateCardFieldsModalComponent,
  RateCardFieldsModalConfig,
} from '../rate-card-fields-modal/rate-card-fields-modal.component'

import { MatDialog } from '@angular/material/dialog'
import { Title } from '@angular/platform-browser'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { AuthService } from '@app/core/service/auth.service'
import { KeyValue } from '@app/core/types/key-value.type'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { authorisedAsChildCompany } from '@app/shared/utils/utils'
import { ApplicationSettingsPage } from '../../const'
import {
  ApplicationSettingCategoryName,
  ApplicationSettingCategoryOrder,
} from '../../const/application-setting-category.enum'
import { RateCardMetaStructureField } from '../../models'
import { ApplicationSettingConfig } from '../../models/application-settings-item.model'

@Component({
  selector: 'application-settings',
  templateUrl: './application-settings.component.html',
  styleUrls: ['./application-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationSettingsComponent {
  @Input() isLoading: boolean
  @Input() settings: KeyValue<ApplicationSettingConfig[]>
  @Input() settingsLoadingMap: KeyValue<boolean>

  @Output() onUpdateSetting = new EventEmitter<ApplicationSettingConfig>()
  @Output() onSaveMetaStructure = new EventEmitter<RateCardMetaStructureField[]>()
  @Output() onClearTrafficImportCache = new EventEmitter<void>()

  menuOptions: MenuOptions[] = []

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private titleService: Title,
    private dialog: MatDialog
  ) {
    this.titleService.setTitle('Application Settings | Admin | Scope')
    this.setMenuOptions()
  }

  isSettingDisabled = (setting: ApplicationSettingConfig) =>
    !setting.isEditEnabled ||
    (setting.dependOnSetting &&
      !this.authService.loggedInUser.company.hasApplicationSetting(setting.dependOnSetting)) ||
    this.settingsLoadingMap[setting.id]

  isSettingActive = (setting: ApplicationSettingConfig) =>
    setting.isEnabled &&
    (!setting.dependOnSetting || this.authService.loggedInUser.company.hasApplicationSetting(setting.dependOnSetting))

  updateSetting(setting: ApplicationSettingConfig, value: boolean) {
    this.onUpdateSetting.emit({ ...setting, isEnabled: value })
  }

  clearTrafficImportCache() {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Clear Traffic Import Cache`,
        body: `This will clear any cache traffic system pages and entities`,
        confirmText: 'Clear',
        cancelText: 'Cancel',
        hasCancelButton: true,
        confirmCallback: () => {
          dialog.close()
          this.onClearTrafficImportCache.emit()
        },
        cancelCallback: () => dialog.close(),
        limitBodyWidth: true,
      },
    })
  }

  editRateCardFields() {
    const data: RateCardFieldsModalConfig = {
      saveFields: (fields: any) => {
        dialog.close()
        this.onSaveMetaStructure.emit(fields)
      },
    }
    const dialog = this.dialog.open(RateCardFieldsModalComponent, { data })
  }

  setMenuOptions() {
    this.menuOptions = [
      {
        name: () => 'Notification profile',
        icon: () => 'insert_chart',
        isHidden: () => !!authorisedAsChildCompany(),
        hasPrivilege: () => Privilege.COMPANY_NOTIFICATION_PROFILE__VIEW,
        callback: () => this.navigateTo(ApplicationSettingsPage.NOTIFICATION_PROFILES),
      },
      {
        name: () => 'Permission profile',
        icon: () => 'view_list',
        hasPrivilege: () => Privilege.COMPANY_MEMBERS_PRIVILEGES__VIEW,
        callback: () => this.navigateTo(ApplicationSettingsPage.PERMISSION_PROFILES),
      },
      {
        name: () => 'Rate card fields',
        icon: () => 'settings',
        isHidden: () => !!authorisedAsChildCompany(),
        hasPrivilege: () => Privilege.COMPANY_MEMBERS_PRIVILEGES__VIEW,
        callback: () => this.editRateCardFields(),
      },
    ]
  }

  private navigateTo(page: ApplicationSettingsPage) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'application', subTab: page },
      replaceUrl: true,
      skipLocationChange: false,
    })
  }

  isTrafficable = () => this.authService.loggedInUser.company.trafficSystemSettings

  protected readonly ApplicationSettingCategoryOrder = ApplicationSettingCategoryOrder
  protected readonly ApplicationSettingCategoryName = ApplicationSettingCategoryName
}
